import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TermLabel } from "../../../api/content/ontologyApi";
import type { ConflictCheck, Review } from "../../../api/content/reviewDecoders";
import { useTermCardsByCommit, type TermCards } from "../../ontology";
import { orderedLabels } from "../../ontology/viewer/ontologyModel";

const NO_NAMES: Readonly<Record<string, string>> = {};

/** A term's name among its labels: the reader's language first, then English, then any (`orderedLabels`); `undefined` without a label. */
export function labelIn(labels: readonly TermLabel[], language: string): string | undefined {
  return orderedLabels(labels, language)[0]?.value;
}

/** The changed terms' names in the reader's language, by IRI (R3-1). */
export function conflictTermNames(check: ConflictCheck | null, language: string): Readonly<Record<string, string>> {
  if (check === null) return NO_NAMES;
  return Object.fromEntries(Object.entries(check.termLabels).flatMap(([iri, labels]) => {
    const name = labelIn(labels, language);
    return name === undefined ? [] : [[iri, name]];
  }));
}

/**
 * The review's term names in the reader's language, by IRI: the conflict check's (R3-1), then the
 * data model's term cards at the change set's model version, its pinned core modules included
 * (usability round 4: a term the change set doesn't label still has its label there). A term
 * with neither has no entry; each place says so in its own words, never by the IRI's end.
 */
export function reviewTermNames(check: ConflictCheck | null, cards: TermCards | null, language: string): Readonly<Record<string, string>> {
  const fromCards = [...(cards?.values() ?? [])].flatMap((card) => {
    const name = labelIn(card.labels, language);
    return name === undefined ? [] : [[card.iri, name] as const];
  });
  return { ...Object.fromEntries(fromCards), ...conflictTermNames(check, language) };
}

/** `reviewTermNames` for `review` at its head, in the app's language, again when either changes; none while there is no review. */
export function useReviewTermNames(review: Review | null): Readonly<Record<string, string>> {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? "en";
  const head = review?.head ?? null;
  const cards = useTermCardsByCommit(review?.changeSet.projectId ?? null, head === null ? [] : [head]).get(head ?? "") ?? null;
  const check = review?.conflictCheck ?? null;
  return useMemo(() => reviewTermNames(check, cards, language), [check, cards, language]);
}
